import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Star, Loader2, LogOut, Check, X, Trash2, Plus, ShieldCheck, RefreshCw,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "xngs_admin_token";
const inputCls = "bg-white border-black/10 text-[#1F1F1F] placeholder:text-[#9ca3af] focus-visible:ring-[#5A189A]";

function authHeader() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } };
}

function Stars({ n }) {
  return (
    <div className="flex gap-0.5 text-[#F4B400]">
      {Array.from({ length: n || 5 }).map((_, i) => <Star key={i} size={15} fill="currentColor" />)}
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Incorrect password" : "Login failed. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7] px-5">
      <form onSubmit={submit} data-testid="admin-login-form"
        className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.25)] border border-black/5">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#5A189A] text-white">
            <ShieldCheck size={26} />
          </span>
          <h1 className="font-display text-2xl font-extrabold text-[#1F1F1F]">Reviews Admin</h1>
          <p className="mt-1 text-sm text-[#9ca3af]">Enter your password to manage reviews.</p>
        </div>
        <Input type="password" data-testid="admin-password" placeholder="Admin password"
          value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} autoFocus />
        <button type="submit" data-testid="admin-login-btn" disabled={loading || !password}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#5A189A] hover:bg-[#3D0A6E] px-6 py-3 text-sm font-semibold text-white disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Log in
        </button>
      </form>
    </div>
  );
}

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function AdminReviews() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ name: "", role: "", rating: 5, quote: "" });
  const [adding, setAdding] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, r] = await Promise.all([
        axios.get(`${API}/admin/reviews?status=pending`, authHeader()),
        axios.get(`${API}/admin/reviews?status=approved`, authHeader()),
        axios.get(`${API}/admin/reviews?status=rejected`, authHeader()),
      ]);
      setCounts({ pending: p.data.length, approved: a.data.length, rejected: r.data.length });
      setReviews({ pending: p.data, approved: a.data, rejected: r.data }[tab]);
    } catch (err) {
      if (err.response?.status === 401) { logout(); return; }
      toast.error("Could not load reviews.");
    } finally { setLoading(false); }
  }, [tab, logout]);

  useEffect(() => { if (authed) loadAll(); }, [authed, loadAll]);

  const setStatus = async (id, action) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/reviews/${id}?action=${action}`, {}, authHeader());
      toast.success(action === "approve" ? "Review approved & published." : "Review rejected.");
      loadAll();
    } catch { toast.error("Action failed."); } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, authHeader());
      toast.success("Review deleted.");
      loadAll();
    } catch { toast.error("Delete failed."); } finally { setBusyId(null); }
  };

  const addReview = async (e) => {
    e.preventDefault();
    if (!form.name || !form.quote) { toast.error("Add a name and review text."); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/reviews`, form, authHeader());
      toast.success("Review added & published.");
      setForm({ name: "", role: "", rating: 5, quote: "" });
      setTab("approved");
      loadAll();
    } catch { toast.error("Could not add review."); } finally { setAdding(false); }
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <header className="sticky top-0 z-10 border-b border-black/5 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#5A189A] text-white"><ShieldCheck size={18} /></span>
            <div>
              <div className="font-display text-lg font-extrabold leading-none text-[#1F1F1F]">Reviews Admin</div>
              <div className="text-[11px] text-[#9ca3af]">XPRESSNGOSHIP LTD</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-medium text-[#1F1F1F] hover:bg-[#F5F5F7]">
              <RefreshCw size={15} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full bg-[#1F1F1F] px-4 py-2 text-sm font-medium text-white hover:bg-black">
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-5 py-8">
        {/* Add review */}
        <form onSubmit={addReview} data-testid="admin-add-form" className="mb-8 rounded-3xl border border-black/5 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[#5A189A]">
            <Plus size={16} /> Add a review directly
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input data-testid="admin-add-name" placeholder="Customer name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <Input data-testid="admin-add-role" placeholder="City / role (optional)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} />
          </div>
          <div className="mt-4 flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4b5563]">Rating</span>
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`admin-star-${n}`} className="p-0.5">
                <Star size={22} className={form.rating >= n ? "text-[#F4B400]" : "text-black/15"} fill={form.rating >= n ? "currentColor" : "none"} />
              </button>
            ))}
          </div>
          <Textarea data-testid="admin-add-quote" rows={3} placeholder="Review text *" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className={`${inputCls} mt-4 resize-none`} />
          <button type="submit" data-testid="admin-add-submit" disabled={adding}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#E31B23] hover:bg-[#B71319] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            {adding ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add & publish
          </button>
        </form>

        {/* Tabs */}
        <div className="mb-5 flex gap-2">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-[#5A189A] text-white" : "border border-black/10 bg-white text-[#1F1F1F] hover:bg-[#F5F5F7]"}`}>
              {t.label}
              <span className={`rounded-full px-2 py-0.5 text-xs ${tab === t.key ? "bg-white/20" : "bg-[#F5F5F7]"}`}>{counts[t.key]}</span>
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#5A189A]"><Loader2 className="animate-spin" /></div>
        ) : reviews.length === 0 ? (
          <div data-testid="admin-empty" className="rounded-3xl border border-dashed border-black/10 bg-white py-20 text-center text-[#9ca3af]">
            No {tab} reviews.
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-review-list">
            {reviews.map((r) => (
              <div key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-[#1F1F1F]">{r.name}</span>
                      {r.role ? <span className="text-xs text-[#9ca3af]">{r.role}</span> : null}
                      <Stars n={r.rating} />
                    </div>
                    <p className="mt-2 text-[15px] leading-relaxed text-[#333]">"{r.quote}"</p>
                    <p className="mt-2 text-[11px] text-[#c0c0c8]">{new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {tab !== "approved" && (
                      <button onClick={() => setStatus(r.id, "approve")} disabled={busyId === r.id} data-testid={`approve-${r.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#1a9c4a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#157d3b] disabled:opacity-60">
                        <Check size={15} /> Approve
                      </button>
                    )}
                    {tab !== "rejected" && (
                      <button onClick={() => setStatus(r.id, "reject")} disabled={busyId === r.id} data-testid={`reject-${r.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E31B23]/30 bg-white px-4 py-2 text-sm font-semibold text-[#E31B23] hover:bg-[#E31B23]/5 disabled:opacity-60">
                        <X size={15} /> Reject
                      </button>
                    )}
                    <button onClick={() => remove(r.id)} disabled={busyId === r.id} data-testid={`delete-${r.id}`}
                      className="inline-flex items-center justify-center rounded-full border border-black/10 bg-white p-2 text-[#9ca3af] hover:text-[#E31B23] hover:border-[#E31B23]/30 disabled:opacity-60">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
